import { resolveApiConfigOrNull } from '../api/client';
import type { BookingState } from '../state/machine';

/**
 * Booking details → the Marketing Cloud funnel row.
 *
 * Step 1 puts an identified customer on a row, and the confirmation marks it
 * complete. Between the two the customer chooses a fitting, a fitter, a day,
 * a time and perhaps a promo code, and none of that used to leave the page:
 * if they walked away, Club Champion knew that they had started and nothing
 * about what they were booking.
 *
 * Each time one of those choices changes, the widget now sends the middleware
 * a SNAPSHOT of all of them, and the middleware writes it onto the session's
 * row. A snapshot rather than a patch, because the reducer clears choices in
 * cascades — a new fitting drops the slot, a new studio drops everything —
 * and a row that kept the old slot would describe a booking nobody was
 * making. Sending everything each time means the row is simply whatever the
 * customer is looking at.
 *
 * The same rules as every other call in this folder: fire-and-forget, never
 * throws, and nothing in the booking flow waits on it.
 */

export interface BookingDetailsSnapshot {
  /** The marketing submissionId — the id step 1 and the booking also send. */
  sessionId: string;
  market: string;
  storeName?: string;
  /** Canonical fitting name, the same value analytics sends as fitting_selection. */
  fittingType?: string;
  /** Present only when the customer chose a specific fitter. */
  fitterId?: string;
  fitterName?: string;
  /** YYYY-MM-DD */
  date?: string;
  /** Slot start in the studio's wall clock (the widget's fake-Z form). */
  slotStart?: string;
  /** The code the customer typed. Never the auto-applied master promo. */
  promoCode?: string;
  promoStatus?: 'valid' | 'invalid' | 'entered';
}

type DetailState = Pick<
  BookingState,
  | 'country'
  | 'selectedLocation'
  | 'selectedFittingType'
  | 'selectedFitterId'
  | 'selectedDate'
  | 'selectedSlot'
  | 'promoCode'
  | 'promoValidation'
  | 'promoValidatedCode'
>;

/** True when the snapshot says anything beyond who and where. */
export function hasDetails(s: BookingDetailsSnapshot): boolean {
  return Boolean(s.fittingType || s.fitterId || s.date || s.slotStart || s.promoCode);
}

/**
 * The customer's current selections, or null when there is no session id to
 * key them on.
 */
export function buildBookingDetails(state: DetailState, sessionId: string | undefined): BookingDetailsSnapshot | null {
  if (!sessionId) return null;

  const fitterId = state.selectedFitterId ?? undefined;
  // "Any fitter" is selectedFitterId === null, and then the slot's fitter is
  // one the widget picked at random — not a choice the customer made, so it
  // is not reported as one. A name is known only once a slot with that same
  // fitter is selected; before that the id travels alone.
  const fitterName =
    fitterId && state.selectedSlot?.fitterId === fitterId ? state.selectedSlot.fitterName || undefined : undefined;

  const promoCode = state.promoCode.trim() || undefined;
  const validated = promoCode !== undefined && state.promoValidatedCode === state.promoCode && state.promoValidation !== null;
  const promoStatus = promoCode === undefined ? undefined : validated ? (state.promoValidation!.valid ? 'valid' : 'invalid') : 'entered';

  return {
    sessionId,
    // Null country is the US-default embed, the same mapping step 1 and the
    // booking use. All three must agree or the middleware's market gate sees
    // one session in two markets.
    market: state.country ?? 'us',
    storeName: state.selectedLocation?.name || undefined,
    fittingType: state.selectedFittingType?.name || undefined,
    fitterId,
    fitterName,
    date: state.selectedDate ?? undefined,
    slotStart: state.selectedSlot?.start ?? undefined,
    promoCode,
    promoStatus,
  };
}

/** POST the snapshot. Undefined fields are left out; the middleware clears whatever is absent. */
export function postBookingDetails(snapshot: BookingDetailsSnapshot): void {
  if (typeof window === 'undefined') return;
  const resolved = resolveApiConfigOrNull();
  if (!resolved) return;
  try {
    void fetch(`${resolved.apiBaseUrl}/analytics/booking-details`, {
      method: 'POST',
      body: JSON.stringify(snapshot),
      // Survives the tab closing — which is exactly when an abandoned
      // booking's last change would otherwise be lost.
      keepalive: true,
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${resolved.bearerToken}`,
      },
    }).catch(() => {
      /* never surfaces */
    });
  } catch {
    // Swallow — reporting never blocks the booking flow.
  }
}

export interface BookingDetailsReporter {
  /** Offer the current snapshot; it is sent after a pause if it differs from the last one sent. */
  update(snapshot: BookingDetailsSnapshot | null): void;
  /** Send whatever is waiting, now. */
  flush(): void;
  /** Forget what was last sent, so the next update goes out even if unchanged. */
  reset(): void;
  /** Drop whatever is waiting. */
  cancel(): void;
}

/**
 * Debounce and de-duplicate.
 *
 * One click can change several things at once (a new fitting clears the slot
 * and re-prices the promo), and a promo code arrives a keystroke at a time.
 * The pause collapses each burst into one call; comparing with the last
 * snapshot sent means a re-render that changed nothing sends nothing.
 *
 * A session that has chosen nothing yet sends nothing — but once something
 * has been sent, an empty snapshot does go out, because that is how the row
 * learns the customer cleared their choices.
 */
export function createBookingDetailsReporter(
  opts: { delayMs?: number; post?: (s: BookingDetailsSnapshot) => void } = {},
): BookingDetailsReporter {
  const delayMs = opts.delayMs ?? 800;
  const post = opts.post ?? postBookingDetails;
  let lastSent: string | null = null;
  let pending: BookingDetailsSnapshot | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const clear = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  const send = () => {
    clear();
    const snapshot = pending;
    pending = null;
    if (!snapshot) return;
    const key = JSON.stringify(snapshot);
    if (key === lastSent) return;
    if (lastSent === null && !hasDetails(snapshot)) return;
    lastSent = key;
    post(snapshot);
  };

  return {
    update(snapshot) {
      if (!snapshot) return;
      pending = snapshot;
      clear();
      timer = setTimeout(send, delayMs);
    },
    flush: send,
    reset() {
      lastSent = null;
    },
    cancel() {
      clear();
      pending = null;
    },
  };
}
